import React, { useEffect, useState } from "react";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { Link } from "react-router-dom";
import { toast } from "../../helpers/swal";
import { useNavigate } from "react-router-dom";

const getRequestedServiceStyle = (service) => {
  const s = service?.toLowerCase() || "";
  if (s.includes("next day")) return { backgroundColor: "#ffcccc" };
  if (s.includes("priority")) return { backgroundColor: "#ffe0b3" };
  if (s.includes("standard")) return { backgroundColor: "#d4edda" };
  if (s.includes("local")) return { backgroundColor: "#d1ecf1" };
  return {};
};

// Nested property okuma fonksiyonu
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc?.[part], obj) || "";
};

// Sıralama ikonunu döndürür
const getSortIcon = (key, currentKey, order) => {
  if (currentKey !== key) return "⇅";
  return order === "asc" ? "⬆️" : "⬇️";
};

// Order'ın complete olup olmadığını kontrol eden fonksiyon
const isOrderComplete = (order) => {
  // Eğer order'da items yoksa veya boşsa incomplete
  if (!order.items || !Array.isArray(order.items) || order.items.length === 0) {
    return false;
  }

  // Discount ve shipping olmayan itemları filtrele
  const validItems = order.items.filter(
    (item) => !["discount", "shipping"].includes(item.name?.toLowerCase() || "")
  );

  // Eğer valid item yoksa incomplete
  if (validItems.length === 0) {
    return false;
  }

  // İlk valid item'ı kontrol et
  const firstItem = validItems[0];

  // SKU, name veya quantity eksikse incomplete
  if (!firstItem.name || !firstItem.quantity) {
    return false;
  }

  return true;
};

const EmbroideryMyOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);
  const navigate = useNavigate();

  // Mağaza ve tag state'leri kaldırıldı - embroidery için gerek yok
  const [selectedNoteFilters, setSelectedNoteFilters] = useState(["all"]);
  const [availableNotes, setAvailableNotes] = useState([]);

  useEffect(() => {
    const savedSortKey = localStorage.getItem("embroideryOrdersSortKey");
    const savedSortOrder = localStorage.getItem("embroideryOrdersSortOrder");

    if (savedSortKey) setSortKey(savedSortKey);
    if (savedSortOrder) setSortOrder(savedSortOrder);
    
    // Sayfa açıldığında order'ları hemen getir
    fetchOrders();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        fetchOrders();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    const updatedDrafts = orders.reduce((acc, order) => {
      acc[order.orderId] = order.internalNotes || "";
      return acc;
    }, {});
    setNoteDrafts(updatedDrafts);
  }, [orders]);

  // Siparişler değiştiğinde available notes'u güncelle
  useEffect(() => {
    if (orders.length > 0) {
      const uniqueNotes = new Set();
      orders.forEach((order) => {
        if (order.internalNotes && order.internalNotes.trim()) {
          uniqueNotes.add(order.internalNotes.trim());
        }
      });
      setAvailableNotes(Array.from(uniqueNotes).sort());
    } else {
      setAvailableNotes([]);
    }
  }, [orders]);

  const handleNoteChange = (orderId, value) => {
    setNoteDrafts((prev) => ({
      ...prev,
      [orderId]: value,
    }));
  };

  const handleCheckboxChange = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Tag değişikliği fonksiyonu - embroidery için basitleştirildi
  const handleTagChange = (orderId, selectedTagId) => {
    // Embroidery kullanıcısı tag değiştiremez, sadece görüntüler
    toast("Embroidery orders cannot modify tags", "info");
  };

  const fetchOrders = () => {
    setLoading(true);
    setOrders([]);

    // Embroidery için özel API endpoint'i
    const url = `${settings.apiURL}/get_embroidery_orders.php`;

    axios
      .get(url, { headers: authHeader() })
      .then((res) => {
        if (res.data.success && Array.isArray(res.data.orders)) {
          // Duplicate/incomplete orderları filtrele
          const completeOrders = res.data.orders.filter(isOrderComplete);
          setOrders(completeOrders);
        } else {
          setOrders([]);
          toast("No embroidery orders found", "info");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching embroidery orders:", err);
        setLoading(false);
        setOrders([]);
        toast("Error fetching embroidery orders", "error");
      });
  };

  const calculateAge = (orderDate) => {
    const now = new Date();
    const texasOffset = -5;
    const orderTime = new Date(orderDate);
    const diffInHours = Math.floor(
      (now.getTime() + texasOffset * 60 * 60 * 1000 - orderTime.getTime()) /
        (1000 * 60 * 60)
    );
    return diffInHours;
  };

  const getAgeCellStyle = (hours) => {
    if (hours < 24) {
      return { backgroundColor: "#d4edda", color: "#155724" };
    } else if (hours < 48) {
      return { backgroundColor: "#fff3cd", color: "#856404" };
    } else {
      return { backgroundColor: "#f8d7da", color: "#721c24" };
    }
  };

  const handleSort = (key) => {
    let newOrder = "asc";
    if (sortKey === key) {
      newOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortKey(key);
    setSortOrder(newOrder);

    localStorage.setItem("embroideryOrdersSortKey", key);
    localStorage.setItem("embroideryOrdersSortOrder", newOrder);
  };

  const handleNoteSave = (orderId) => {
    const note = noteDrafts[orderId]?.trim() || "";

    setSavingNoteId(orderId);

    const noteToSave = note === "" ? null : note;

    axios
      .post(
        `${settings.apiURL}/update_order_notes.php`,
        { orderId, internalNote: noteToSave },
        { headers: authHeader() }
      )
      .then((res) => {
        if (res.data.success) {
          toast("Internal note updated", "success");
          setOrders((prev) =>
            prev.map((order) =>
              order.orderId === orderId
                ? { ...order, internalNotes: noteToSave }
                : order
            )
          );
        } else {
          toast("Failed to update note", "error");
        }
      })
      .catch(() => {
        toast("Error updating note", "error");
      })
      .finally(() => {
        setSavingNoteId(null);
      });
  };

  const handleNoteFilterChange = (noteValue) => {
    if (noteValue === "all") {
      setSelectedNoteFilters(["all"]);
    } else {
      const newFilters = selectedNoteFilters.includes("all")
        ? [noteValue]
        : selectedNoteFilters.includes(noteValue)
        ? selectedNoteFilters.filter((f) => f !== noteValue)
        : [...selectedNoteFilters.filter((f) => f !== "all"), noteValue];

      if (newFilters.length === 0) {
        setSelectedNoteFilters(["all"]);
      } else {
        setSelectedNoteFilters(newFilters);
      }
    }
  };

  const truncateNote = (note, maxLength = 25) => {
    if (!note) return "";
    return note.length > maxLength
      ? note.substring(0, maxLength) + "..."
      : note;
  };

  const sortedOrders = [...orders].sort((a, b) => {
    if (!sortKey) return 0;

    const getVal = (obj, key) => {
      if (key === "quantity") {
        const filtered =
          obj.items?.filter(
            (item) =>
              !["discount", "shipping"].includes(item.name.toLowerCase())
          ) || [];
        return filtered.reduce((sum, item) => sum + (item.quantity || 0), 0);
      }

      if (key === "age") {
        return calculateAge(obj.createDate);
      }

      if (key === "designStatus") {
        if (obj.isPlaced) return 2;
        if (obj.hasDesign) return 1;
        return 0;
      }

      return getNestedValue(obj, key)?.toString().toLowerCase() || "";
    };

    const valA = getVal(a, sortKey);
    const valB = getVal(b, sortKey);

    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;

    const skuA = getNestedValue(a, "items.0.sku").toLowerCase();
    const skuB = getNestedValue(b, "items.0.sku").toLowerCase();
    return skuA.localeCompare(skuB);
  });

  const filteredOrders = sortedOrders.filter((order) => {
    const matchesSearch =
      order.orderNumber?.toString().includes(searchQuery) ||
      order.shipTo?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesNoteFilter = (() => {
      if (selectedNoteFilters.includes("all")) return true;

      const orderNote = order.internalNotes?.trim() || "";

      if (selectedNoteFilters.includes("empty") && !orderNote) return true;

      return selectedNoteFilters.some(
        (filter) =>
          filter !== "all" && filter !== "empty" && orderNote === filter
      );
    })();

    return matchesSearch && matchesNoteFilter;
  });

  return (
    <div className="container-fluid p-4" style={{ fontSize: "13px" }}>
      {/* 🏠 Home butonu ortalanmış başlıkla birlikte */}
      <div className="text-center mb-4">
        <h2>Embroidery Orders</h2>
        <Link to="/home" className="btn btn-outline-primary mt-2">
          🏠 Home
        </Link>
      </div>
      
      <div className="mb-2" style={{ fontSize: "14px" }}>
        <strong>Total Embroidery Orders:</strong> {filteredOrders.length}
      </div>

      <div className="d-flex gap-2 mb-3">
        <input
          className="form-control w-auto"
          type="text"
          placeholder="Search by Order # or Customer"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <button className="btn btn-primary" onClick={() => fetchOrders()}>
          Refresh Orders
        </button>

        <button
          className="btn btn-outline-secondary"
          onClick={() => {
            if (!searchQuery.trim()) {
              toast("Please enter a search term", "warning");
              return;
            }

            // Special search for embroidery orders
            const searchResults = filteredOrders.filter(order =>
              order.orderNumber?.toString().includes(searchQuery) ||
              order.shipTo?.name?.toLowerCase().includes(searchQuery.toLowerCase())
            );

            if (searchResults.length > 0) {
              const firstOrder = searchResults[0];
              navigate(`/order-details/${firstOrder.orderId}`, {
                state: { orders: [firstOrder] },
              });
            } else {
              toast("No matching embroidery orders found", "info");
            }
          }}
        >
          🔍 Search
        </button>
      </div>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading embroidery orders...</p>
        </div>
      ) : (
        <div
          style={{ overflowX: "auto", overflowY: "auto", maxHeight: "700px" }}
        >
          <table className="table table-bordered table-hover table-sm">
            <thead
              className="table-light"
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 10,
              }}
            >
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      filteredOrders.length > 0 &&
                      selectedOrders.length === filteredOrders.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allIds = filteredOrders.map(
                          (order) => order.orderId
                        );
                        setSelectedOrders(allIds);
                      } else {
                        setSelectedOrders([]);
                      }
                    }}
                  />
                </th>

                <th>#</th>
                <th
                  onClick={() => handleSort("orderNumber")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Order # {getSortIcon("orderNumber", sortKey, sortOrder)}
                </th>
                <th
                  onClick={() => handleSort("age")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Age {getSortIcon("age", sortKey, sortOrder)}
                </th>
                <th
                  onClick={() => handleSort("shipTo.name")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Recipient {getSortIcon("shipTo.name", sortKey, sortOrder)}
                </th>
                

                <th>
                  Note
                  <select
                    value={
                      selectedNoteFilters.includes("all")
                        ? "all"
                        : selectedNoteFilters.length === 1
                        ? selectedNoteFilters[0]
                        : "multiple"
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "all") {
                        setSelectedNoteFilters(["all"]);
                      } else if (value === "empty") {
                        setSelectedNoteFilters(["empty"]);
                      } else {
                        setSelectedNoteFilters([value]);
                      }
                    }}
                    style={{ marginLeft: "6px" }}
                  >
                    <option value="all">All Notes</option>
                    <option value="empty">Empty Notes</option>
                    {availableNotes.map((note, index) => (
                      <option key={index} value={note}>
                        {truncateNote(note, 20)}
                      </option>
                    ))}
                    {selectedNoteFilters.length > 1 &&
                      !selectedNoteFilters.includes("all") && (
                        <option value="multiple">Multiple Selected</option>
                      )}
                  </select>
                </th>

                <th>Tags</th>

                <th
                  onClick={() => handleSort("quantity")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Quantity {getSortIcon("quantity", sortKey, sortOrder)}
                </th>
                <th>Gift</th>
               
                <th>Requested Service</th>
                <th
                  onClick={() => handleSort("createDate")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Order Date {getSortIcon("createDate", sortKey, sortOrder)}
                </th>
               

                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("designStatus")}
                >
                  Status{" "}
                  {sortKey === "designStatus"
                    ? sortOrder === "asc"
                      ? "↑"
                      : "↓"
                    : ""}
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.map((order, index) => {
                const itemsFiltered =
                  order.items?.filter(
                    (item) =>
                      !["discount", "shipping"].includes(
                        item.name.toLowerCase()
                      )
                  ) || [];
                const totalQuantity = itemsFiltered.reduce(
                  (sum, item) => sum + (item.quantity || 0),
                  0
                );
                const firstItem = itemsFiltered[0] || {};
                const ageHours = calculateAge(order.createDate);

                return (
                  <tr key={order.orderId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.orderId)}
                        onChange={() => handleCheckboxChange(order.orderId)}
                      />
                    </td>
                    <td>{index + 1}</td>
                    <td
                      style={{
                        maxWidth: "50px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={order.orderNumber}
                    >
                      <Link
                        to={`/order-details/${order.orderId}`}
                        state={{ orders: filteredOrders }}
                        style={{ color: "blue", textDecoration: "underline" }}
                      >
                        {order.orderNumber}
                      </Link>
                    </td>
                    <td
                      style={{
                        ...getAgeCellStyle(ageHours),
                        maxWidth: "20px",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {ageHours} hr
                    </td>
                    <td
                      style={{
                        maxWidth: "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={order.shipTo?.name || "-"}
                    >
                      {order.shipTo?.name || "-"}
                    </td>
                    

                    <td style={{ width: "200px", padding: "4px 8px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          style={{
                            width: "125px",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            marginRight: "5px",
                          }}
                          maxLength={200}
                          value={noteDrafts[order.orderId] || ""}
                          onChange={(e) =>
                            handleNoteChange(order.orderId, e.target.value)
                          }
                          title={
                            noteDrafts[order.orderId] ||
                            order.internalNotes ||
                            "-"
                          }
                        />
                        <button
                          className="btn btn-sm btn-success"
                          style={{ fontSize: "12px", padding: "4px 8px" }}
                          onClick={() => handleNoteSave(order.orderId)}
                          disabled={savingNoteId === order.orderId}
                        >
                          Save
                        </button>
                      </div>
                    </td>
                    <td
                      style={{
                        maxWidth: "100px",
                        backgroundColor: "#006400",
                        color: "white",
                      }}
                    >
                      <select
                        className="form-select form-select-sm"
                        value={order.tagIds?.[0] ?? ""}
                        onChange={(e) =>
                          handleTagChange(order.orderId, e.target.value)
                        }
                        style={{
                          backgroundColor: "#006400",
                          color: "white",
                        }}
                        disabled={true} // Embroidery kullanıcısı tag değiştiremez
                      >
                        <option value="">7-Embroidery</option>
                      </select>
                    </td>
                    <td style={{ maxWidth: "20px", textAlign: "center" }}>
                      {totalQuantity}
                    </td>
                    <td>{order.gift ? "🎁" : "-"}</td>
                   
                    <td
                      style={getRequestedServiceStyle(
                        order.requestedShippingService
                      )}
                    >
                      {order.requestedShippingService || "-"}
                    </td>
                    <td>
                      {new Date(order.createDate).toLocaleDateString("en-US")}
                    </td>
                    

                    <td
                      style={{
                        backgroundColor:
                          order.is_placed === true
                            ? "#198754"
                            : order.design_uploaded === true
                            ? "#198754"
                            : "inherit",
                        color:
                          order.is_placed === true ||
                          order.design_uploaded === true
                            ? "white"
                            : "inherit",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {order.is_placed === true
                        ? "Placed"
                        : order.design_uploaded === true
                        ? "Uploaded"
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filteredOrders.length === 0 && !loading && (
        <div className="text-center mt-4">
          <p>No embroidery orders found.</p>
        </div>
      )}
    </div>
  );
};

export default EmbroideryMyOrdersPage;